import { useEffect, useState } from 'react'
import { ContactAddressType, ExternalNumber } from '../common/ContactAddress.type'
import { InputTextType } from '../common/InputType'
import { CONTACT_FIELD_MAX_LENGTH } from '../util/contactFieldLimits'

export type ContactBasicInfo = {
  state: {
    lastName: string
    firstName: string
    lastNameKana: string
    firstNameKana: string
    email: string
    externalNumbers: ExternalNumber[]
  }

  lastNameInput: InputTextType
  firstNameInput: InputTextType
  lastNameKanaInput: InputTextType
  firstNameKanaInput: InputTextType
  emailInput: InputTextType

  externalNumbers: ExternalNumber[]
  addExternalNumber: () => void
  updateExternalNumber: (index: number, partial: Partial<ExternalNumber>) => void
  removeExternalNumber: (index: number) => void

  showLabelListIndex: number | null
  setShowLabelListIndex: React.Dispatch<React.SetStateAction<number | null>>
}


const mergeLegacyNumbers = (contactAddress?: ContactAddressType): ExternalNumber[] => {
  const base = contactAddress?.externalNumbers ?? []

  const exists = new Set(
    base
      .map((x) => (x.externalPhoneNumber ?? '').trim())
      .filter((x) => x !== ''),
  )

  const merged: ExternalNumber[] = [...base]

  const publicNum = (contactAddress?.phone ?? '').trim()
  if (publicNum !== '' && !exists.has(publicNum)) {
    merged.unshift({
      externalNumberId: undefined,
      externalPhoneNumber: publicNum,
      labelCode: 1,
      customLabel: '',
      displayOrder: 1,
      updCount: undefined,
    })
    exists.add(publicNum)
  }

  const mobileNum = (contactAddress?.mobilePhone ?? '').trim()
  if (mobileNum !== '' && !exists.has(mobileNum)) {
    const insertIndex = publicNum !== '' ? 1 : 0
    merged.splice(insertIndex, 0, {
      externalNumberId: undefined,
      externalPhoneNumber: mobileNum,
      labelCode: 4,
      customLabel: '',
      displayOrder: insertIndex + 1,
      updCount: undefined,
    })
    exists.add(mobileNum)
  }

  return merged.map((row, i) => ({
    ...row,
    displayOrder: i + 1,
  }))
}

/* =====================
 * Hook
 * ===================== */
const useContactBasicInfo = (
  contactAddress?: ContactAddressType,
): ContactBasicInfo => {
  /* ---------- basic fields ---------- */
  const [lastName, setLastName] = useState(
    contactAddress?.lastName ?? contactAddress?.contactName ?? '',
  )
  const [firstName, setFirstName] = useState(contactAddress?.firstName ?? '')
  const [lastNameKana, setLastNameKana] = useState(contactAddress?.lastNameKana ?? '')
  const [firstNameKana, setFirstNameKana] = useState(contactAddress?.firstNameKana ?? '')
  const [email, setEmail] = useState(contactAddress?.email ?? '')

  /* ---------- external numbers ---------- */
  const [externalNumbers, setExternalNumbers] = useState<ExternalNumber[]>([
    {
      externalNumberId: undefined,
      externalPhoneNumber: '',
      labelCode: null,
      customLabel: '',
      displayOrder: 1,
      updCount: undefined,
    },
  ])

  /* ---------- UI only ---------- */
  const [showLabelListIndex, setShowLabelListIndex] = useState<number | null>(null)

  const addExternalNumber = () => {
    setExternalNumbers((prev) => {
      const nextOrder = prev.length + 1
      return [
        ...prev,
        {
          externalNumberId: undefined,
          externalPhoneNumber: '',
          labelCode: null,
          customLabel: '',
          displayOrder: nextOrder,
          updCount: undefined,
        },
      ]
    })
  }

  const updateExternalNumber = (
    index: number,
    partial: Partial<ExternalNumber>,
  ) => {
    setExternalNumbers((prev) => {
      const copy = [...prev]
      copy[index] = { ...copy[index], ...partial }
      return copy
    })
  }

  const removeExternalNumber = (index: number) => {
    setExternalNumbers((prev) =>
      prev
        .filter((_, i) => i !== index)
        .map((row, i) => ({ ...row, displayOrder: i + 1 })),
    )
  }

  const lastNameInput: InputTextType = {
    type: 'text',
    id: 'lastName',
    label: '姓',
    placeholder: '姓',
    required: true,
    autoComplete: 'family-name',
    state: lastName,
    setState: setLastName,
    readonly: false,
    maxLength: CONTACT_FIELD_MAX_LENGTH.lastName,
  }

  const firstNameInput: InputTextType = {
    type: 'text',
    id: 'firstName',
    label: '名',
    placeholder: '名',
    required: false,
    autoComplete: 'given-name',
    state: firstName,
    setState: setFirstName,
    readonly: false,
    maxLength: CONTACT_FIELD_MAX_LENGTH.firstName,
  }

  const lastNameKanaInput: InputTextType = {
    type: 'text',
    id: 'lastNameKana',
    label: '',
    placeholder: 'ふりがな（姓）',
    required: false,
    autoComplete: 'none',
    state: lastNameKana,
    setState: setLastNameKana,
    readonly: false,
    maxLength: CONTACT_FIELD_MAX_LENGTH.lastNameKana,
  }

  const firstNameKanaInput: InputTextType = {
    type: 'text',
    id: 'firstNameKana',
    label: '',
    placeholder: 'ふりがな（名）',
    required: false,
    autoComplete: 'none',
    state: firstNameKana,
    setState: setFirstNameKana,
    readonly: false,
    maxLength: CONTACT_FIELD_MAX_LENGTH.firstNameKana,
  }

  const emailInput: InputTextType = {
    type: 'email',
    id: 'email',
    label: 'Eメール',
    placeholder: '',
    required: false,
    autoComplete: 'email',
    state: email,
    setState: setEmail,
    readonly: false,
    maxLength: CONTACT_FIELD_MAX_LENGTH.email,
  }

  useEffect(() => {
    if (contactAddress?.lastName) setLastName(contactAddress.lastName)
  }, [contactAddress?.lastName])

  useEffect(() => {
    if (contactAddress?.firstName) setFirstName(contactAddress.firstName)
  }, [contactAddress?.firstName])

  useEffect(() => {
    if (contactAddress?.lastNameKana) setLastNameKana(contactAddress.lastNameKana)
  }, [contactAddress?.lastNameKana])

  useEffect(() => {
    if (contactAddress?.firstNameKana) setFirstNameKana(contactAddress.firstNameKana)
  }, [contactAddress?.firstNameKana])

  useEffect(() => {
    if (contactAddress?.email) setEmail(contactAddress.email)
  }, [contactAddress?.email])

  useEffect(() => {
    const merged = mergeLegacyNumbers(contactAddress)

    if (merged && merged.length > 0) {
      setExternalNumbers(merged)
      return
    }

    setExternalNumbers([
      {
        externalNumberId: undefined,
        externalPhoneNumber: '',
        labelCode: null,
        customLabel: '',
        displayOrder: 1,
        updCount: undefined,
      },
    ])
  }, [contactAddress])

  return {
    state: {
      lastName,
      firstName,
      lastNameKana,
      firstNameKana,
      email,
      externalNumbers,
    },

    lastNameInput,
    firstNameInput,
    lastNameKanaInput,
    firstNameKanaInput,
    emailInput,

    externalNumbers,
    addExternalNumber,
    updateExternalNumber,
    removeExternalNumber,

    showLabelListIndex,
    setShowLabelListIndex,
  }
}

export default useContactBasicInfo
